import React, { useState } from "react";
import { TransitionMotion, spring, presets } from "react-motion";

import Portal from "components/utils/Portal";

import DialogPropTypes from "./DialogPropTypes";
import DialogElement from "./DialogElement";
import { Mask, Wrapper } from "./DialogStyles";

interface dialogItemPropTypes {}

const Dialog = (props: DialogPropTypes) => {
  const { children, onCancel, visible, maskClosable, afterClose } = props;

  const openTimeRef = React.useRef<any>(null);
  const visibleRef = React.useRef<any>(null);
  const wrapperRef = React.useRef<any>(null);

  const [dialogItems, setDialogItems] = useState<any>([]);

  visibleRef.current = visible;

  React.useEffect(() => {
    if (visible) {
      setDialogItems([{ key: "mask" }, { key: "dialog" }]);
    } else {
      setDialogItems([]);
    }
    if (visible && !openTimeRef.current) {
      openTimeRef.current = Date.now();
    }
  }, [visible]);

  const onMaskClick = (e) => {
    const openTime = openTimeRef.current;
    if (Date.now() - openTime < 300) {
      return;
    }
    close(e);

    openTimeRef.current = null;
  };

  const close = (e: any) => {
    if (onCancel) {
      onCancel(e);
    }
  };

  const getDefaultStyles = () => {
    return dialogItems.map((item: any) => {
      if (item.key === "mask") {
        return { ...item, style: { opacity: 0 } };
      }
      if (item.key === "dialog") {
        return { ...item, style: { opacity: 0, scale: 0 } };
      }
      return item;
    });
  };

  const getStyles = () => {
    return dialogItems.map((item) => {
      if (item.key === "mask") {
        return {
          ...item,
          style: {
            opacity: spring(1, presets.stiff),
          },
        };
      }
      if (item.key === "dialog") {
        return {
          ...item,
          style: {
            opacity: spring(1, presets.gentle),
            scale: spring(1, presets.stiff),
          },
        };
      }
      return item;
    });
  };

  const willEnter = () => {
    return { opacity: 0, scale: 0 };
  };
  const willLeave = () => {
    return {
      opacity: spring(0, presets.stiff),
      scale: spring(0, presets.stiff),
    };
  };

  const didLeave = () => {
    if (afterClose) {
      afterClose();
    }
  };

  return (
    <TransitionMotion
      defaultStyles={getDefaultStyles()}
      styles={getStyles()}
      willEnter={willEnter}
      willLeave={willLeave}
      didLeave={didLeave}
    >
      {(translatedStyle: any) => {
        return (
          <>
            {translatedStyle.map(({ key, style }) => {
              if (key === "mask") {
                return (
                  <Mask
                    key={key}
                    style={{
                      opacity: style.opacity,
                      display: style.opacity < 0.1 ? "none" : "block",
                    }}
                  />
                );
              }
              if (key === "dialog") {
                return (
                  <Wrapper
                    key={key}
                    ref={wrapperRef}
                    onClick={maskClosable ? onMaskClick : null}
                    bg={"transparent"}
                    style={{
                      display:
                        visible || style.opacity > 0.1 ? "block" : "none",
                    }}
                  >
                    <DialogElement
                      scale={style.scale}
                      {...props}
                      onCancel={close}
                    >
                      {children}
                    </DialogElement>
                  </Wrapper>
                );
              }
              return null;
            })}
          </>
        );
      }}
    </TransitionMotion>
  );
};

Dialog.defaultProps = {
  maskClosable: true,
};

const DialogWrapper = React.memo<any>((props: DialogPropTypes) => {
  const { visible } = props;

  return (
    <Portal rootID="modal-root" visible={visible}>
      <Dialog {...props} />
    </Portal>
  );
});

export default DialogWrapper;

export { Dialog };
